import React, { useEffect } from 'react';
import { Card, Form, Input, InputNumber, Space, message } from 'antd';
import type { QaqcPipePipesize } from '../../../../types';
import { insertData, updateData, fetchSqlData } from '../../../../services/api';
import Button from '../../../../components/common/Button';

export type QaqcPipePipesizeMode = 'create' | 'edit';

interface QaqcPipePipesizeCreateProps {
  mode?: QaqcPipePipesizeMode;
  initialValues?: Partial<QaqcPipePipesize> | null;
  onSaved?: (saved: QaqcPipePipesize | any) => void;
  onCancel?: () => void;
  onBack?: () => void;
  viewOnly?: boolean;
}

type FormValues = {
  project_id?: number | string;
  pipesize_name: string;
  pipesize_inch: number;
};

const QaqcPipePipesizeCreate: React.FC<QaqcPipePipesizeCreateProps> = ({
  mode = 'create',
  initialValues,
  onSaved,
  onCancel,
  onBack,
  viewOnly,
}) => {
  const [form] = Form.useForm<FormValues>();
  const isEdit = mode === 'edit';
  const isViewOnly = !!viewOnly;

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        project_id: initialValues.project_id ?? 1,
        pipesize_name: initialValues.pipesize_name,
        pipesize_inch: initialValues.pipesize_inch as any,
      } as any);
    } else {
      form.setFieldsValue({ project_id: 1 } as any);
    }
  }, [initialValues, form]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const normalizeName = (raw: string): string => {
        let s = String(raw || '').trim();
        if (!s.endsWith('"')) s = s + '"';
        return s;
      };
      const name = normalizeName(values.pipesize_name);
      if (name.length > 6) {
        message.error('Pipe Size must be at most 6 characters including the ending "');
        return;
      }
      const payload: any = {
        project_id: Number(values.project_id) || 1,
        pipesize_name: name,
        pipesize_inch: Number(values.pipesize_inch),
      };

      if (isEdit && initialValues?.id) {
        await updateData('qaqc_pipe_pipesize', initialValues.id, payload, 'id');
        message.success('Pipe size updated');
        try { window.dispatchEvent(new Event('qaqc_pipe_pipesize:changed')); } catch {}
        onSaved?.({ id: initialValues.id, ...payload });
      } else {
        const resp = await insertData('qaqc_pipe_pipesize', payload);
        message.success('Pipe size created');
        try { window.dispatchEvent(new Event('qaqc_pipe_pipesize:changed')); } catch {}
        onSaved?.({ id: resp?.ids?.[0], ...payload });
        form.resetFields();
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      const msg = e?.response?.data?.error || e?.message || 'Save failed';
      message.error(msg);
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 18 }}>
            {(isEdit || isViewOnly) && onBack && (
              <a onClick={onBack} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            )}
            {isViewOnly ? 'View Pipe Size' : isEdit ? 'Edit Pipe Size' : 'Create Pipe Size'}
          </span>
        </div>
      }
      bordered
      size="small"
    >
      <Form form={form} layout="vertical" disabled={isViewOnly}>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Space style={{ gap: 12, display: 'flex', flexWrap: 'wrap' }}>
            <Form.Item label="Project" name="project_id" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
              <Input style={{ width: 120 }} />
            </Form.Item>
            <Form.Item label={'Pipe Size (must end with \" )'} name="pipesize_name" rules={[{ required: true, max: 6 }]} style={{ marginBottom: 0 }}>
              <Input maxLength={6} style={{ width: 160 }} />
            </Form.Item>
            <Form.Item label="Inch" name="pipesize_inch" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
              <InputNumber style={{ width: 160 }} step={0.0001} min={0} />
            </Form.Item>
          </Space>

          {!isViewOnly && (
            <Space>
              <Button action={isEdit ? 'edit' : 'add'} type="primary" onClick={onSubmit}>{isEdit ? 'Update' : 'Create'}</Button>
              {onCancel && <Button onClick={onCancel}>Cancel</Button>}
            </Space>
          )}
        </Space>
      </Form>
    </Card>
  );
};

export default QaqcPipePipesizeCreate;
